/**
 * Viridis colormap (9 piecewise-linear control points, ported from the
 * prototype) expanded to a 256-entry RGB LUT for fast canvas rendering,
 * plus a light sequential map for coloring regions by permittivity.
 */
const VIRIDIS: [number, number, number, number][] = [
  [0.0, 68, 1, 84],
  [0.125, 72, 35, 116],
  [0.25, 64, 67, 135],
  [0.375, 52, 94, 141],
  [0.5, 33, 145, 140],
  [0.625, 53, 183, 121],
  [0.75, 109, 205, 89],
  [0.875, 180, 222, 44],
  [1.0, 253, 231, 37],
];

function sample(t: number): [number, number, number] {
  t = Math.max(0, Math.min(1, t));
  for (let i = 0; i < VIRIDIS.length - 1; i++) {
    const [t0, r0, g0, b0] = VIRIDIS[i]!;
    const [t1, r1, g1, b1] = VIRIDIS[i + 1]!;
    if (t <= t1) {
      const f = (t - t0) / (t1 - t0);
      return [Math.round(r0 + f * (r1 - r0)), Math.round(g0 + f * (g1 - g0)), Math.round(b0 + f * (b1 - b0))];
    }
  }
  return [253, 231, 37];
}

/** 256-entry RGB LUT (rgba stride 3). */
export const VIRIDIS_LUT: Uint8Array = (() => {
  const lut = new Uint8Array(256 * 3);
  for (let i = 0; i < 256; i++) {
    const [r, g, b] = sample(i / 255);
    lut[i * 3] = r;
    lut[i * 3 + 1] = g;
    lut[i * 3 + 2] = b;
  }
  return lut;
})();

export function viridisCss(t: number): string {
  const [r, g, b] = sample(t);
  return `rgb(${r},${g},${b})`;
}

/**
 * Region color by relative permittivity: air ≈ white, higher εr a deeper
 * blue-gray (log scale to εr = 12). Distinct from the viridis field map so
 * schematic and field views read differently.
 */
export function epsilonColor(er: number): string {
  const t = Math.max(0, Math.min(1, Math.log(Math.max(1, er)) / Math.log(12)));
  const l = 97 - 45 * t;
  return `hsl(210, ${25 + 35 * t}%, ${l}%)`;
}
